import type { ReactNode } from 'react';

import { formatNumber, seriesColor } from '@/usage/format';
import type { ChartSeries, Metric } from '@/usage/series';

import styles from './UsageKeyTable.module.css';

export interface UsageKeyTableProps {
  series: ChartSeries[];
  metric: Metric;
  /** Wraps a key's name in a link to it; routing is the caller's. */
  renderName?: (series: ChartSeries, name: ReactNode) => ReactNode;
}

/** The chart's legend and its table view: each key's totals over the range. */
export function UsageKeyTable({ series, metric, renderName }: UsageKeyTableProps) {
  const total = (field: 'totalCredits' | 'totalRequests') =>
    series.reduce((sum, entry) => sum + entry[field], 0);
  const metricTotal = total(metric === 'credits' ? 'totalCredits' : 'totalRequests');

  return (
    <table className={styles.table}>
      <caption className={styles.caption}>Usage by API key</caption>
      <thead>
        <tr>
          <th scope="col">API key</th>
          <th scope="col" className={styles.number}>
            Requests
          </th>
          <th scope="col" className={styles.number}>
            Credits
          </th>
          <th scope="col" className={styles.share}>
            Share of {metric}
          </th>
        </tr>
      </thead>
      <tbody>
        {series.map((entry) => {
          const value = metric === 'credits' ? entry.totalCredits : entry.totalRequests;
          const share = metricTotal > 0 ? value / metricTotal : 0;
          const name = <span className={styles.name}>{entry.label}</span>;
          return (
            <tr key={entry.id}>
              <th scope="row">
                <span className={styles.keyCell}>
                  <span
                    className={styles.swatch}
                    style={{ background: seriesColor(entry) }}
                    aria-hidden="true"
                  />
                  {renderName && entry.slot !== null ? renderName(entry, name) : name}
                  {entry.revoked ? <span className={styles.tag}>Revoked</span> : null}
                  {entry.slot === null ? (
                    <span className={styles.tag}>{entry.keyIds.length} keys</span>
                  ) : null}
                </span>
              </th>
              <td className={styles.number}>{formatNumber(entry.totalRequests)}</td>
              <td className={styles.number}>{formatNumber(entry.totalCredits)}</td>
              <td className={styles.share}>
                <span className={styles.bar} aria-hidden="true">
                  <span style={{ width: `${share * 100}%`, background: seriesColor(entry) }} />
                </span>
                <span className={styles.percent}>{Math.round(share * 100)}%</span>
              </td>
            </tr>
          );
        })}
      </tbody>
      {series.length > 1 ? (
        <tfoot>
          <tr>
            <th scope="row">Total</th>
            <td className={styles.number}>{formatNumber(total('totalRequests'))}</td>
            <td className={styles.number}>{formatNumber(total('totalCredits'))}</td>
            <td className={styles.share} />
          </tr>
        </tfoot>
      ) : null}
    </table>
  );
}
